import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Output,
  EventEmitter,
  inject,
  Renderer2,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  DestroyRef,
} from '@angular/core';
import { DOCUMENT, CommonModule } from '@angular/common';
import { BehaviorSubject, distinctUntilChanged } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LucideAngularModule, X, BookOpen, Target, List } from 'lucide-angular';
import { InstructionalModel } from '../../services/instructional-models';

@Component({
  selector: 'app-model-info-popover',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './model-info-popover.html',
  styleUrl: './model-info-popover.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModelInfoPopoverComponent implements AfterViewInit, OnDestroy {
  private readonly renderer = inject(Renderer2);
  private readonly elementRef = inject(ElementRef);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  private readonly isOpen$$ = new BehaviorSubject<boolean>(false);
  protected readonly isOpen$ = this.isOpen$$.asObservable();

  private readonly model$$ = new BehaviorSubject<InstructionalModel | null>(null);
  protected readonly model$ = this.model$$.asObservable();

  private isAppendedToBody = false;

  @Input()
  set isOpen(value: boolean) {
    this.isOpen$$.next(value);
  }

  @Input()
  set model(value: InstructionalModel | null) {
    this.model$$.next(value);
  }

  @Output() readonly closed = new EventEmitter<void>();

  protected readonly icons = {
    X,
    BookOpen,
    Target,
    List,
  };

  ngAfterViewInit(): void {
    // Watch for open state changes and move element to/from body
    this.isOpen$$.pipe(
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((isOpen) => {
      if (isOpen) {
        this.appendToBody();
      } else {
        this.removeFromBody();
      }
    });
  }

  ngOnDestroy(): void {
    this.removeFromBody();
  }

  private appendToBody(): void {
    if (!this.isAppendedToBody) {
      this.renderer.appendChild(this.document.body, this.elementRef.nativeElement);
      this.isAppendedToBody = true;
    }
  }

  private removeFromBody(): void {
    if (this.isAppendedToBody) {
      this.renderer.removeChild(this.document.body, this.elementRef.nativeElement);
      this.isAppendedToBody = false;
    }
  }

  protected onClose(): void {
    this.closed.emit();
  }
}
